import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  Bell,
  CalendarClock,
  CheckCheck,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  FileCheck,
  MoreHorizontal,
  ShieldAlert,
  SlidersHorizontal,
} from "lucide-react";
import { cn } from "@/shared/lib/cn";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  useToast,
} from "@/shared/ui";
import {
  useInAppNotificationsInfiniteQuery,
  useInAppNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from "@/modules/sys/queries/useSysQueries";
import {
  resolveNotificationRoute,
  type InAppNotification,
} from "@/modules/sys/model/types";
import { formatNotificationInboxItem } from "@/modules/sys/model/notificationInboxView";

function getNotificationCircularIcon(
  eventType: string,
  severity: string,
  isUnread = false,
) {
  const isHigh =
    severity === "high" || severity === "critical" || severity === "danger";
  const isWarn = severity === "medium" || severity === "warning";

  let Icon = Bell;
  if (eventType === "LotHeld") Icon = ShieldAlert;
  else if (eventType === "QcPending") Icon = ClipboardCheck;
  else if (eventType === "ProductionDelay") Icon = AlertTriangle;
  else if (eventType === "OperationStarted" || eventType === "OperationReady")
    Icon = Activity;
  else if (eventType === "BatchCompleted") Icon = CheckCircle2;
  else if (eventType === "ExpiryWarning") Icon = CalendarClock;
  else if (eventType === "ApprovalPending") Icon = FileCheck;
  else if (isHigh || isWarn) Icon = AlertTriangle;

  return (
    <div
      className={cn(
        "flex size-9 shrink-0 items-center justify-center rounded-full shadow-2xs transition-all",
        isUnread
          ? isHigh
            ? "bg-danger-soft text-danger"
            : isWarn
              ? "bg-warning-soft text-warning"
              : "bg-primary-soft text-primary"
          : "bg-bg text-text-muted/80 opacity-75 group-hover:opacity-100",
      )}
    >
      <Icon className="size-4.5" aria-hidden />
    </div>
  );
}

export type OpsNotificationPopoverProps = {
  unreadCount: number;
};

/**
 * Facebook-style notification popover on Ops Topbar Bell.
 * Supports:
 * - Mobile-style filter pills ("Tất cả", "Chưa đọc")
 * - Infinite scroll / scroll-more
 * - "Xem tất cả" button at bottom & section header
 * - Deep linking on item click + auto mark-as-read
 */
export function OpsNotificationPopover({ unreadCount }: OpsNotificationPopoverProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Query "Chưa đọc" full count / fast access
  const unreadQuery = useInAppNotificationsQuery({
    unreadOnly: true,
    take: 200,
    refetchIntervalMs: open ? 30_000 : false,
  });

  // Query dạng infinite scroll cho tab hiện tại
  const infiniteQuery = useInAppNotificationsInfiniteQuery({
    unreadOnly: filter === "unread",
  });

  const markRead = useMarkNotificationReadMutation();
  const markAll = useMarkAllNotificationsReadMutation();

  const allItems = useMemo(
    () => infiniteQuery.data?.pages.flatMap((page) => page) ?? [],
    [infiniteQuery.data],
  );

  const displayedList = useMemo(() => {
    if (filter === "unread") {
      return (unreadQuery.data ?? []).filter((n) => !n.isRead);
    }
    return allItems;
  }, [filter, unreadQuery.data, allItems]);

  const rows = useMemo(
    () =>
      displayedList.map((n) => ({
        raw: n,
        view: formatNotificationInboxItem(n),
      })),
    [displayedList],
  );

  const hasNextPage = filter === "all" && Boolean(infiniteQuery.hasNextPage);
  const isFetchingNextPage = infiniteQuery.isFetchingNextPage;

  // Infinite scroll trigger khi cuộn gần cuối danh sách
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (filter !== "all") return;
    const target = e.currentTarget;
    if (
      target.scrollHeight - target.scrollTop - target.clientHeight < 80 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      void infiniteQuery.fetchNextPage();
    }
  };

  const handleItemClick = async (item: InAppNotification) => {
    if (!item.isRead) {
      void markRead.mutateAsync(item.id);
    }
    setOpen(false);
    const route = resolveNotificationRoute(item, "ops");
    if (route) {
      navigate(route);
    } else {
      navigate("/ops/sys/notifications");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAll.mutateAsync();
      toast({
        tone: "success",
        title: "Thành công",
        message: "Đã đánh dấu tất cả thông báo là đã đọc.",
      });
    } catch {
      toast({
        tone: "error",
        title: "Lỗi",
        message: "Không thể cập nhật trạng thái đã đọc.",
      });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span className="relative inline-flex">
          <IconButton
            label={unreadCount > 0 ? `Thông báo · ${unreadCount} chưa đọc` : "Thông báo của tôi"}
            icon={<Bell className="size-4" />}
            size="md"
            aria-expanded={open}
          />
          {unreadCount > 0 ? (
            <span
              className={cn(
                "pointer-events-none absolute -top-1 -right-1 flex h-4 items-center justify-center rounded-full bg-danger font-bold leading-none text-white ring-2 ring-surface shadow-xs font-[family-name:var(--font-badge)] tabular-nums",
                unreadCount < 10
                  ? "w-4 text-[10px]"
                  : "min-w-4 px-1 text-[9.5px] tracking-tight",
              )}
              aria-hidden
            >
              {unreadCount > 99 ? "+99" : unreadCount}
            </span>
          ) : null}
        </span>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={3}
        className="relative -top-[13px] w-[22rem] sm:w-[25rem] p-0 shadow-2xl bg-surface border border-border rounded-xl flex flex-col overflow-visible"
      >
        <PopoverArrow width={14} height={7} />

        {/* Header: Title + ... Menu */}
        <div className="flex items-center justify-between px-3.5 pt-2 pb-1">
          <h2 className="m-0 text-[15px] font-bold text-text leading-tight">Thông báo</h2>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="size-7 rounded-full flex items-center justify-center text-text-muted hover:text-text hover:bg-bg cursor-pointer transition-colors"
                aria-label="Tùy chọn thông báo"
              >
                <MoreHorizontal className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 p-1 shadow-lg">
              <DropdownMenuItem
                disabled={unreadCount === 0 || markAll.isPending}
                className="cursor-pointer gap-2 py-2 text-caption font-medium"
                onSelect={handleMarkAllRead}
              >
                <CheckCheck className="size-4 text-text-muted" />
                <span>Đánh dấu tất cả là đã đọc</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer gap-2 py-2 text-caption font-medium"
                onSelect={() => {
                  setOpen(false);
                  navigate("/ops/sys/notify-prefs");
                }}
              >
                <SlidersHorizontal className="size-4 text-text-muted" />
                <span>Cài đặt thông báo</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer gap-2 py-2 text-caption font-medium"
                onSelect={() => {
                  setOpen(false);
                  navigate("/ops/sys/notifications");
                }}
              >
                <ExternalLink className="size-4 text-text-muted" />
                <span>Mở toàn trang Thông báo</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Tab Filters (Mobile Pill Style) */}
        <div className="flex items-center gap-1.5 px-3.5 pb-2 pt-0.5 border-b border-border/50">
          <button
            type="button"
            onClick={() => setFilter("all")}
            className={cn(
              "rounded-full px-3 py-0.5 text-[12px] font-semibold transition-all cursor-pointer",
              filter === "all"
                ? "bg-primary-soft text-primary font-bold shadow-2xs"
                : "text-text-secondary hover:bg-bg hover:text-text",
            )}
          >
            Tất cả
          </button>
          <button
            type="button"
            onClick={() => setFilter("unread")}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-3 py-0.5 text-[12px] font-semibold transition-all cursor-pointer",
              filter === "unread"
                ? "bg-primary-soft text-primary font-bold shadow-2xs"
                : "text-text-secondary hover:bg-bg hover:text-text",
            )}
          >
            <span>Chưa đọc</span>
            {unreadCount > 0 ? (
              <span className="rounded-full bg-danger px-1.5 py-0.2 text-[10px] font-bold text-white tabular-nums font-mono">
                {unreadCount > 99 ? "+99" : unreadCount}
              </span>
            ) : null}
          </button>
        </div>

        {/* Notification List with Infinite Scroll */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="max-h-[22rem] sm:max-h-[26rem] min-h-[10rem] overflow-y-auto px-2 py-1.5 space-y-1 divide-y divide-border/30"
        >
          {infiniteQuery.isLoading && rows.length === 0 ? (
            <div className="py-12 text-center text-text-muted text-caption">
              Đang tải thông báo…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-text-muted text-caption">
              {filter === "unread"
                ? "Bạn đã đọc hết tất cả thông báo"
                : "Chưa có thông báo nào"}
            </div>
          ) : (
            rows.map(({ raw, view }) => (
              <div
                key={raw.id}
                onClick={() => void handleItemClick(raw)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    void handleItemClick(raw);
                  }
                }}
                className={cn(
                  "group flex items-start gap-3 rounded-lg p-2.5 transition-all cursor-pointer text-left relative",
                  !raw.isRead
                    ? "bg-primary-soft/50 hover:bg-primary-soft/75 shadow-2xs"
                    : "bg-surface hover:bg-bg/60 opacity-80 hover:opacity-100",
                )}
              >
                {/* Circular Icon */}
                {getNotificationCircularIcon(raw.eventType, raw.severity, !raw.isRead)}

                {/* Content */}
                <div className="min-w-0 flex-1 pr-1">
                  <div className="text-[13px] leading-snug line-clamp-3">
                    <span
                      className={cn(
                        !raw.isRead
                          ? "font-bold text-text"
                          : "font-medium text-text-secondary",
                      )}
                    >
                      {view.title}
                    </span>
                    {view.summary ? (
                      <span
                        className={cn(
                          !raw.isRead
                            ? "text-text font-normal"
                            : "text-text-muted font-normal",
                        )}
                      >
                        {" "}· {view.summary}
                      </span>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={cn(
                        "text-[11.5px] tabular-nums",
                        !raw.isRead
                          ? "font-semibold text-primary"
                          : "text-text-muted",
                      )}
                    >
                      {view.whenLabel}
                    </span>
                    {raw.plantCode ? (
                      <span className="font-mono text-[10.5px] text-text-muted">
                        · {raw.plantCode}
                      </span>
                    ) : null}
                    {!raw.isRead ? (
                      <span className="rounded bg-primary/15 text-primary text-[10px] font-bold px-1.5 py-0.2 leading-tight">
                        Mới
                      </span>
                    ) : null}
                  </div>
                </div>

                {/* Right: Unread Indicator Dot */}
                <div className="flex items-center self-center shrink-0 pl-1">
                  {!raw.isRead ? (
                    <span
                      className="size-2.5 rounded-full bg-primary shrink-0 transition-transform group-hover:scale-125"
                      title="Chưa đọc"
                    />
                  ) : null}
                </div>
              </div>
            ))
          )}

          {/* Load More Trigger on Scroll / Button */}
          {hasNextPage ? (
            <div className="py-2.5 text-center">
              <button
                type="button"
                onClick={() => void infiniteQuery.fetchNextPage()}
                disabled={isFetchingNextPage}
                className="text-caption font-semibold text-primary hover:underline cursor-pointer disabled:opacity-50"
              >
                {isFetchingNextPage ? "Đang tải thêm…" : "Tải thêm thông báo…"}
              </button>
            </div>
          ) : rows.length > 0 ? (
            <p className="py-2 text-center text-[11px] text-text-muted m-0">
              Đã hiển thị tất cả
            </p>
          ) : null}
        </div>

        {/* Footer: Xem tất cả thông báo */}
        <div className="p-2 border-t border-border bg-surface rounded-b-xl">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="w-full justify-center font-medium shadow-2xs"
            onClick={() => {
              setOpen(false);
              navigate("/ops/sys/notifications");
            }}
          >
            Xem tất cả thông báo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
